import * as React from "react";
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  Chip,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { Close } from "@mui/icons-material";
import { ErrorAlert, SuccessAlert } from "../../utils/Alerts";
import {
  useDeleteIframeMutation,
  useGetAllItemsQuery,
  useGetAmazonListQuery,
  useGetIframesQuery,
  useGetStylesQuery,
} from "../../Slices/roomNateApis";
import { useState } from "react";
import ViewAmazon from "./ViewAmazon";
import AddAmazon from "./AddAmazon";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import UpdateAmazon from "./UpdateAmazon";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { useGlobalAuthContext } from "../../context/AuthContext";
import { useGlobalContext } from "../../context/GlobalContext";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function resolveItemName(itemRef, itemsList) {
  const id = typeof itemRef === "string" ? itemRef : itemRef?._id;
  const found = itemsList?.find((x) => x._id === id);
  if (found?.name) return found.name;
  if (itemRef?.name) {
    const n = itemRef.name;
    return n.charAt(0).toUpperCase() + n.slice(1).toLowerCase();
  }
  return id || "—";
}

function resolveStyleLabel(styleId, styles) {
  const found = styles?.find((s) => s._id === styleId);
  if (found?.name) return found.name;
  if (typeof styleId === "string") {
    return styleId.charAt(0).toUpperCase() + styleId.slice(1).toLowerCase();
  }
  return String(styleId ?? "");
}

function AmazonIframeCardSkeleton() {
  return (
    <Card
      elevation={0}
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        overflow: "hidden",
      }}
    >
      <Stack direction={{ xs: "column", sm: "row" }}>
        <Skeleton variant="rectangular" sx={{ width: { xs: "100%", sm: 148 }, height: { xs: 200, sm: 261 } }} />
        <Stack spacing={1} sx={{ flex: 1, p: 2 }}>
          <Skeleton width="60%" />
          <Skeleton width="40%" />
          <Stack direction="row" gap={0.5}>
            <Skeleton width={56} height={24} />
            <Skeleton width={72} height={24} />
          </Stack>
        </Stack>
      </Stack>
      <Divider />
      <Box sx={{ px: 1.5, py: 1, display: "flex", justifyContent: "flex-end", gap: 1 }}>
        <Skeleton variant="circular" width={34} height={34} />
        <Skeleton variant="circular" width={34} height={34} />
        <Skeleton variant="circular" width={34} height={34} />
      </Box>
    </Card>
  );
}

export default function ListIFrames() {
  const { currentUserSessionStorage } = useGlobalAuthContext();
  const { activePartner } = useGlobalContext();

  const { data: FetchItems, isFetching: itemsFetching } = useGetAllItemsQuery(
    { itemType: 1, ...(activePartner ? { activePartner } : {}) },
    { skip: !currentUserSessionStorage }
  );

  const { data: FetchStyles } = useGetStylesQuery();
  const {
    data: FetchIframes,
    isLoading: iframesLoading,
    isFetching: iframesFetching,
  } = useGetIframesQuery(undefined, {
    skip: !currentUserSessionStorage,
  });

  const {
    data: amazonListData,
    isLoading: amazonListLoading,
    isFetching: amazonListFetching,
  } = useGetAmazonListQuery(undefined, {
    skip: !currentUserSessionStorage,
  });

  const [selectedId, setSelectedId] = React.useState(null);
  const [open, setOpen] = React.useState(false);
  const [deleteIframe, resultStats] = useDeleteIframeMutation();
  const [successAlert, setSuccessAlert] = React.useState(false);
  const [errorAlert, setErrorAlert] = React.useState(false);

  const [itemIds, setItemIds] = useState([]);
  const [styleIds, setStyleIds] = useState([]);

  const [isDragging, setIsDragging] = useState(false);
  const [activeContainerIdx, setActiveContainerIdx] = useState(null);
  const containerRefs = React.useRef([]);
  const startXRef = React.useRef(null);
  const startScrollRefs = React.useRef([]);

  const slideWidth = 128;
  const marginRight = 10;

  const partnerItemIdSet = React.useMemo(() => {
    if (!activePartner) return null;
    return new Set((FetchItems?.items ?? []).map((i) => i._id));
  }, [FetchItems, activePartner]);

  const passesPartnerFilter = React.useCallback(
    (row) => {
      if (!activePartner) return true;
      if (!partnerItemIdSet || partnerItemIdSet.size === 0) return false;
      const ids = (row?.items ?? [])
        .map((it) => (typeof it === "string" ? it : it?._id))
        .filter(Boolean);
      return ids.some((id) => partnerItemIdSet.has(id));
    },
    [activePartner, partnerItemIdSet]
  );

  const filteredData = React.useMemo(() => {
    const list = FetchIframes ?? [];
    const afterPartner = list.filter(passesPartnerFilter);
    return afterPartner.filter((item) => {
      const styleVals = item.styles ?? [];
      const itemIdStrs = (item.items ?? []).map((it) =>
        String((typeof it === "string" ? it : it?._id) ?? "").toLowerCase()
      );

      const itemsMatch =
        itemIds.length === 0 ||
        itemIds.some((itemId) => itemIdStrs.includes(String(itemId).toLowerCase()));

      const stylesMatch =
        styleIds.length === 0 ||
        styleIds.some((styleId) => styleVals.map(String).includes(String(styleId)));

      return itemsMatch && stylesMatch;
    });
  }, [FetchIframes, passesPartnerFilter, itemIds, styleIds]);

  const handleTouchMove = (e, idx) => {
    if (!isDragging) return;
    const x = e.touches[0].clientX;
    const deltaX = startXRef.current - x;
    containerRefs.current[idx].scrollLeft = startScrollRefs.current[idx] + deltaX;
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
    setActiveContainerIdx(null);
  };

  const handleTouchStart = (e, idx) => {
    setIsDragging(true);
    setActiveContainerIdx(idx);
    startXRef.current = e.touches[0].clientX;
    startScrollRefs.current = containerRefs.current.map((ref) => ref.scrollLeft);
  };

  const handleMouseDown = (e, idx) => {
    setIsDragging(true);
    setActiveContainerIdx(idx);
    startXRef.current = e.clientX;
    startScrollRefs.current = containerRefs.current.map((ref) => ref.scrollLeft);
  };

  const handleMouseMove = React.useCallback(
    (e) => {
      if (!isDragging || activeContainerIdx === null) return;
      const x = e.clientX;
      const deltaX = startXRef.current - x;
      containerRefs.current[activeContainerIdx].scrollLeft =
        startScrollRefs.current[activeContainerIdx] + deltaX;
    },
    [isDragging, activeContainerIdx]
  );

  const handleMouseUp = React.useCallback(() => {
    setIsDragging(false);
    setActiveContainerIdx(null);
  }, []);

  React.useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  const handleClickOpen = (id) => {
    setOpen(true);
    setSelectedId(id);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };

  React.useEffect(() => {
    if (resultStats?.isSuccess) {
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
      }, 2000);
    }

    if (resultStats?.isError) {
      setErrorAlert(true);
      setTimeout(() => {
        setErrorAlert(false);
      }, 2000);
    }
  }, [resultStats]);

  const handleDelete = () => {
    if (selectedId) deleteIframe(selectedId);
    handleClose();
  };

  const handleScrollLeft = (index) => {
    containerRefs.current[index].scrollLeft -= slideWidth + marginRight;
  };

  const handleScrollRight = (index) => {
    containerRefs.current[index].scrollLeft += slideWidth + marginRight;
  };

  const iframesBusy = iframesLoading || iframesFetching;
  const itemsBusyForPartner = Boolean(activePartner) && itemsFetching;
  const showIframeSkeletons = iframesBusy || itemsBusyForPartner;

  return (
    <>
      {successAlert && (
        <SuccessAlert title="Iframe has been deleted successfully!" />
      )}

      {errorAlert && <ErrorAlert title="Something Went Wrong!" />}

      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 3,
          bgcolor: "background.paper",
        }}
      >
        <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 2 }}>
          Search filters
        </Typography>
        <Grid container spacing={2}>
          {FetchStyles && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                onChange={(_, value) => {
                  if (value) {
                    setStyleIds((prev) =>
                      value.includes(undefined) ? prev : value.map((v) => v._id)
                    );
                  }
                }}
                name="styles"
                multiple
                options={FetchStyles}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option._id}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} name="styles" label="Styles" placeholder="Select styles" size="small" />
                )}
              />
            </Grid>
          )}
          {FetchItems && (
            <Grid item xs={12} sm={6}>
              <Autocomplete
                onChange={(_, value) => {
                  if (value) {
                    setItemIds((prev) =>
                      value.includes(undefined) ? prev : value.map((v) => v._id)
                    );
                  }
                }}
                name="items"
                multiple
                options={FetchItems?.items ?? []}
                disableCloseOnSelect
                getOptionLabel={(option) => option.name}
                renderOption={(props, option, { selected }) => (
                  <li {...props} key={option._id}>
                    <Checkbox
                      icon={icon}
                      checkedIcon={checkedIcon}
                      style={{ marginRight: 8 }}
                      checked={selected}
                    />
                    {option.name}
                  </li>
                )}
                renderInput={(params) => (
                  <TextField {...params} name="items" label="Items" placeholder="Select items" size="small" />
                )}
              />
            </Grid>
          )}
        </Grid>
      </Box>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={2}
        sx={{ mb: 2 }}
      >
        <Typography variant="h6" component="h2" fontWeight={700}>
          Amazon embeds
        </Typography>
        <AddAmazon FetchItems={FetchItems} FetchStyles={FetchStyles} />
      </Stack>

      {showIframeSkeletons ? (
        <Stack spacing={2} sx={{ mb: 4 }}>
          {[0, 1, 2].map((k) => (
            <AmazonIframeCardSkeleton key={k} />
          ))}
        </Stack>
      ) : !filteredData?.length ? (
        <Typography color="text.secondary" sx={{ py: 4, mb: 4 }}>
          {activePartner
            ? "No Amazon embeds for the selected partner."
            : "No records."}
        </Typography>
      ) : (
        <Stack spacing={2} sx={{ mb: 4, width: "100%", maxWidth: "100%" }}>
          {filteredData.map((row, index) => (
            <Card
              key={row._id || index}
              elevation={0}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 3,
                overflow: "hidden",
                transition: "box-shadow 0.15s, border-color 0.15s",
                "&:hover": {
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  borderColor: "text.disabled",
                },
              }}
            >
              <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
                <Stack direction={{ xs: "column", sm: "row" }} alignItems="stretch">
                  <Box
                    sx={{
                      flexShrink: 0,
                      width: { xs: "100%", sm: 148 },
                      minHeight: { xs: 200, sm: 261 },
                      bgcolor: "grey.50",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: { xs: 1.5, sm: 1 },
                      borderBottom: { xs: "1px solid", sm: "none" },
                      borderColor: "divider",
                      borderRight: { sm: "1px solid" },
                    }}
                  >
                    <Box
                      component="iframe"
                      srcDoc={row.amazonFields?.amazonIframe}
                      title="Amazon embed"
                      sandbox="allow-popups allow-scripts allow-modals allow-forms allow-same-origin"
                      sx={{
                        width: 128,
                        height: 261,
                        maxWidth: "100%",
                        border: 0,
                        display: "block",
                      }}
                    />
                  </Box>

                  <Stack spacing={1.25} sx={{ flex: 1, p: 2, minWidth: 0 }}>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Items
                      </Typography>
                      <Stack spacing={0.5}>
                        {(row?.items ?? []).map((it, i) => (
                          <Typography key={i} variant="body2" fontWeight={600}>
                            {resolveItemName(it, FetchItems?.items)}
                          </Typography>
                        ))}
                      </Stack>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                        Styles
                      </Typography>
                      <Stack direction="row" flexWrap="wrap" gap={0.5}>
                        {(row?.styles ?? []).map((sid, i) => (
                          <Chip
                            key={i}
                            label={resolveStyleLabel(sid, FetchStyles)}
                            size="small"
                            variant="outlined"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </CardContent>
              <Divider />
              <CardActions
                sx={{
                  px: 1.5,
                  py: 1,
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                  gap: 0.5,
                }}
              >
                <Tooltip title="Delete">
                  <IconButton size="small" onClick={() => handleClickOpen(row._id)} color="primary">
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <ViewAmazon item={row} FetchItems={FetchItems} FetchStyles={FetchStyles} />
                <UpdateAmazon item={row} FetchItems={FetchItems} FetchStyles={FetchStyles} />
              </CardActions>
            </Card>
          ))}
        </Stack>
      )}

      <Dialog
        maxWidth="sm"
        fullWidth
        open={open}
        onClose={handleClose}
        aria-labelledby="iframe-delete-title"
        aria-describedby="iframe-delete-desc"
      >
        <DialogTitle id="iframe-delete-title">Confirm the action?</DialogTitle>
        <Box position="absolute" top={10} right={0}>
          <IconButton onClick={handleClose}>
            <Close />
          </IconButton>
        </Box>
        <DialogContent>
          <DialogContentText id="iframe-delete-desc">
            Do you really want to delete this iframe?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="outlined">
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" color="error" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <Typography variant="h6" component="h2" fontWeight={700} sx={{ mb: 2, mt: 1 }}>
        Amazon listings
      </Typography>

      {amazonListLoading || amazonListFetching ? (
        <Stack spacing={2}>
          {[0, 1].map((k) => (
            <Card key={k} elevation={0} sx={{ border: "1px solid", borderColor: "divider", borderRadius: 3 }}>
              <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ p: 2 }}>
                <Skeleton variant="text" width={160} />
                <Skeleton variant="rectangular" height={200} sx={{ flex: 1 }} />
              </Stack>
            </Card>
          ))}
        </Stack>
      ) : !amazonListData?.length ? (
        <Typography color="text.secondary" sx={{ py: 2 }}>
          No listing rows.
        </Typography>
      ) : (
        <Box mt={0}>
          {amazonListData.map((item, idx) => {
            return (
              <Card
                key={idx}
                elevation={0}
                sx={{
                  mb: idx < amazonListData.length - 1 ? 2 : 0,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 3,
                  overflow: "hidden",
                }}
              >
                <Stack
                  direction={{ xs: "column", md: "row" }}
                  spacing={2}
                  alignItems={{ xs: "stretch", md: "flex-start" }}
                  sx={{ p: 2 }}
                >
                  <Box sx={{ minWidth: 0, maxWidth: { md: 200 } }}>
                    <Typography variant="subtitle2" fontWeight={700}>
                      {item.name}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      position: "relative",
                      flex: 1,
                      minWidth: 0,
                      width: "100%",
                    }}
                  >
                    <Box
                      display="flex"
                      height={261}
                      ref={(el) => {
                        containerRefs.current[idx] = el;
                      }}
                      sx={{
                        overflowX: "auto",
                        overflowY: "hidden",
                        "&::-webkit-scrollbar": { height: 8 },
                        scrollbarWidth: "thin",
                      }}
                      onTouchStart={(e) => handleTouchStart(e, idx)}
                      onTouchMove={(e) => handleTouchMove(e, idx)}
                      onTouchEnd={handleTouchEnd}
                      onMouseDown={(e) => handleMouseDown(e, idx)}
                    >
                      <IconButton
                        sx={{
                          position: "absolute",
                          top: "50%",
                          left: { xs: 4, md: -36 },
                          transform: "translateY(-50%)",
                          zIndex: 1,
                          display: {
                            xs: "none",
                            md: item?.furnitureItems?.length >= 7 ? "inline-flex" : "none",
                          },
                          height: 40,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                        }}
                        color="primary"
                        aria-label="Scroll left"
                        onClick={() => handleScrollLeft(idx)}
                        size="small"
                      >
                        <ArrowBackIosNewIcon fontSize="small" />
                      </IconButton>

                      {item?.furnitureItems?.map((fi, idx2) => {
                        if (!fi.embedField) return null;
                        return (
                          <Box
                            key={idx2}
                            sx={{
                              width: slideWidth,
                              minWidth: slideWidth,
                              height: 261,
                              mr: `${marginRight}px`,
                              pointerEvents: isDragging ? "none" : "auto",
                            }}
                          >
                            <Box
                              component="iframe"
                              srcDoc={`<html><head><style>body { margin: 0; }</style></head><body>${fi.embedField}</body></html>`}
                              title="Listing embed"
                              sandbox="allow-popups allow-scripts allow-modals allow-forms allow-same-origin"
                              sx={{
                                width: slideWidth,
                                height: 261,
                                border: 0,
                                display: "block",
                                pointerEvents: isDragging ? "none" : "auto",
                              }}
                            />
                          </Box>
                        );
                      })}

                      <IconButton
                        sx={{
                          position: "absolute",
                          top: "50%",
                          right: { xs: 4, md: -36 },
                          transform: "translateY(-50%)",
                          zIndex: 1,
                          display: {
                            xs: "none",
                            md: item?.furnitureItems?.length >= 7 ? "inline-flex" : "none",
                          },
                          height: 40,
                          bgcolor: "background.paper",
                          boxShadow: 1,
                        }}
                        color="primary"
                        aria-label="Scroll right"
                        onClick={() => handleScrollRight(idx)}
                        size="small"
                      >
                        <ArrowForwardIosIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Stack>
              </Card>
            );
          })}
        </Box>
      )}
    </>
  );
}
